import { apiFetch } from './http';
import { memberBookingRequestId } from './member';

// ─────────────────────────────────────────────────────────────────────
// ONE VENUE, AS A PLAYER WHO IS NOT ITS MEMBER SEES IT (#833).
//
// `GET /venues/:slug` — the public face of one organization: its name, kind,
// the opening hours its calendar enforces, and its courts. Authenticated with
// the account session the player app already holds; it answers the same for
// every caller, because there is nothing in it that depends on who asks.
//
// NOT A BOOKING READ. There is no availability and no price here; both come
// from `readVenueAvailability` below (#833 slices 2 and 3a).
// ─────────────────────────────────────────────────────────────────────

export interface VenueCourt {
  number: number;
  /** The centre's own name for the court, or null for «Bane N». */
  name: string | null;
  type: 'double' | 'single';
}

export interface Venue {
  slug: string;
  name: string;
  /** `CENTRE` or `CLUB`, to label it. */
  kind: string;
  /** Null when the centre has never configured its booking calendar. */
  openingHours: { opens: string; closes: string } | null;
  courts: VenueCourt[];
}

/**
 * The outcome, with «does not exist» and «could not ask» kept apart — the
 * same line `searchClubsOutcome` draws, for the same reason: telling somebody
 * a venue does not exist because the server did not answer is a lie about the
 * venue. A STRING discriminant, because `strict: false` does not narrow a
 * boolean-literal union (see `clubs.ts`).
 */
export type VenueOutcome =
  | { status: 'ok'; venue: Venue }
  | { status: 'not_found' }
  | { status: 'aborted' }
  | { status: 'unavailable' };

const UNAVAILABLE = { status: 'unavailable' } as const;

export async function readVenue(slug: string, signal?: AbortSignal): Promise<VenueOutcome> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch(`/venues/${encodeURIComponent(slug)}`, { signal });
  } catch {
    if (signal?.aborted) return { status: 'aborted' };
    return UNAVAILABLE;
  }
  if (res.status === 404) return { status: 'not_found' };
  if (!res.ok) return UNAVAILABLE;
  const body = (await res.json().catch(() => null)) as { venue?: unknown } | null;
  if (signal?.aborted) return { status: 'aborted' };
  const venue = toVenue(body?.venue);
  return venue ? { status: 'ok', venue } : UNAVAILABLE;
}

/** SHAPED, NOT TRUSTED — a venue without a slug or name is not one to show. */
function toVenue(raw: unknown): Venue | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.slug !== 'string' || !r.slug) return null;
  if (typeof r.name !== 'string' || !r.name) return null;
  const hours = r.openingHours as { opens?: unknown; closes?: unknown } | null | undefined;
  const openingHours =
    hours && typeof hours.opens === 'string' && typeof hours.closes === 'string'
      ? { opens: hours.opens, closes: hours.closes }
      : null;
  const courts: VenueCourt[] = [];
  for (const c of Array.isArray(r.courts) ? r.courts : []) {
    const court = c as Record<string, unknown> | null;
    if (!court || !Number.isFinite(court.number)) continue;
    courts.push({
      number: Number(court.number),
      name: typeof court.name === 'string' && court.name ? court.name : null,
      type: court.type === 'single' ? 'single' : 'double',
    });
  }
  return {
    slug: r.slug,
    name: r.name,
    kind: typeof r.kind === 'string' ? r.kind : '',
    openingHours,
    courts,
  };
}

// ─────────────────────────────────────────────────────────────────────
// WHAT IS FREE THERE (#833 slice 2).
//
// `GET /venues/:slug/availability?date=` — one day's free start times at the
// venue, and the courts a booking there could go on, by the MEMBER booking's
// own rules: the server runs the same slot engine `POST /member/bookings`
// checks against, so a time shown here is one a member could take.
//
// No booking and no person. Since #833 slice 3a (owner decision 2026-11-04)
// it also carries the CALLER'S terms and price: `booking` says their tier at
// this venue and whether the booking door takes them, and each slot its price
// at that tier — the figure the booking command would store. DISPLAY ONLY: the
// door decides the tier again under its locks, and the booking's stored price
// is what is charged. Shown as the server sent it; never computed here.
// ─────────────────────────────────────────────────────────────────────

export interface VenueFreeSlot {
  startTime: string;
  endTime: string;
  /** Court NUMBERS — `Venue.courts[].number`. */
  courts: number[];
  /**
   * What booking this slot costs the caller, whole kroner, at `booking.tier` —
   * the server's quote. Null when booking is not offered to them. Never a price
   * the client worked out.
   */
  priceNok: number | null;
}

/**
 * The booking door's own refusal codes, as the server sends them (worded by the
 * screen). `no_player`: the account has no player profile yet (#1368).
 */
export type VenueBookingRefusal =
  'no_player' | 'guest_booking_not_offered' | 'court_prices_not_set';

/** The caller's terms at this venue — DISPLAY ONLY (the booking door decides again). */
export interface VenueBookingTerms {
  tier: 'member' | 'guest';
  offered: boolean;
  /** Present only when `offered` is false. */
  reason?: VenueBookingRefusal;
}

export interface VenueAvailability {
  /** The day answered, venue-local `YYYY-MM-DD`. */
  date: string;
  /** Every day the server answers for, from the venue's today. */
  dates: string[];
  /** The length every slot is for: the one asked for, or the shortest the venue allows. */
  durationMin: number;
  /** Every length the venue allows. */
  durations: number[];
  booking: VenueBookingTerms;
  slots: VenueFreeSlot[];
}

/** `availability: null` is the server saying the venue has no calendar to show. */
export type VenueAvailabilityOutcome =
  | { status: 'ok'; availability: VenueAvailability | null }
  | { status: 'not_found' }
  | { status: 'aborted' }
  | { status: 'unavailable' };

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const HHMM = /^\d{2}:\d{2}$/;

/**
 * One day of the venue's free courts; `date` omitted asks for the venue's today,
 * `durationMin` omitted for its shortest length.
 */
export async function readVenueAvailability(
  slug: string,
  date?: string,
  signal?: AbortSignal,
  durationMin?: number,
): Promise<VenueAvailabilityOutcome> {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (durationMin !== undefined) params.set('durationMin', String(durationMin));
  // Not `params.size`: older Android WebViews (the member app's shell) lack it.
  const qs = params.toString();
  const query = qs ? `?${qs}` : '';
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch(`/venues/${encodeURIComponent(slug)}/availability${query}`, { signal });
  } catch {
    if (signal?.aborted) return { status: 'aborted' };
    return UNAVAILABLE;
  }
  if (res.status === 404) return { status: 'not_found' };
  if (!res.ok) return UNAVAILABLE;
  const body = (await res.json().catch(() => null)) as { availability?: unknown } | null;
  if (signal?.aborted) return { status: 'aborted' };
  if (!body || !('availability' in body)) return UNAVAILABLE;
  if (body.availability === null) return { status: 'ok', availability: null };
  const availability = toAvailability(body.availability);
  return availability ? { status: 'ok', availability } : UNAVAILABLE;
}

/** SHAPED, NOT TRUSTED — a day that is not a day is not one to show. */
function toAvailability(raw: unknown): VenueAvailability | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.date !== 'string' || !DAY.test(r.date)) return null;
  const dates = (Array.isArray(r.dates) ? r.dates : []).filter(
    (d): d is string => typeof d === 'string' && DAY.test(d),
  );
  const booking = toTerms(r.booking);
  const slots: VenueFreeSlot[] = [];
  for (const s of Array.isArray(r.slots) ? r.slots : []) {
    const slot = s as Record<string, unknown> | null;
    if (!slot || typeof slot.startTime !== 'string' || !HHMM.test(slot.startTime)) continue;
    if (typeof slot.endTime !== 'string' || !HHMM.test(slot.endTime)) continue;
    const courts = (Array.isArray(slot.courts) ? slot.courts : []).filter((c): c is number =>
      Number.isInteger(c),
    );
    // A PRICE ONLY WHERE BOOKING IS OFFERED, and only a whole, non-negative
    // number of kroner. Anything else is «no price», never a guess.
    const price = slot.priceNok;
    const priceNok =
      booking.offered && typeof price === 'number' && Number.isInteger(price) && price >= 0
        ? price
        : null;
    if (courts.length > 0) {
      slots.push({ startTime: slot.startTime, endTime: slot.endTime, courts, priceNok });
    }
  }
  return {
    date: r.date,
    dates,
    durationMin: Number.isFinite(r.durationMin) ? Number(r.durationMin) : 0,
    durations: (Array.isArray(r.durations) ? r.durations : []).filter(
      (d): d is number => Number.isInteger(d) && d > 0,
    ),
    booking,
    slots,
  };
}

const REFUSALS: readonly VenueBookingRefusal[] = [
  'no_player',
  'guest_booking_not_offered',
  'court_prices_not_set',
];

/**
 * The caller's terms, shaped. FAIL-SAFE: anything the client cannot read is
 * «not offered», so a malformed answer can never put a «Book» or a price on
 * screen. An unknown tier is a guest — the tier only labels, the door prices.
 */
function toTerms(raw: unknown): VenueBookingTerms {
  const b = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const tier = b.tier === 'member' ? 'member' : 'guest';
  if (b.offered === true) return { tier, offered: true };
  const reason = REFUSALS.find((x) => x === b.reason);
  return reason ? { tier, offered: false, reason } : { tier, offered: false };
}

// ─────────────────────────────────────────────────────────────────────
// BOOKING THERE, WITH ONE'S OWN ACCOUNT (#833 slice 3b).
//
// `POST /venues/:slug/bookings` — the venue's own door (#1361). The request
// names the court, the day, the start and the length, and NOTHING ELSE: not a
// price, not a tier, not a payment status, not a player. The server finds the
// person from the session, the venue from the slug, and decides member or
// guest from the venue's roster inside its own transaction. What comes back is
// what it stored: the booking, the price it will charge, the tier that price
// was taken at, and — while it is unpaid — how long the court is held.
//
// THE KEY IS THE MEMBER BOOKING'S OWN (`memberBookingRequestId`), derived from
// the slot: a retry of the same booking carries the same key, so a lost answer
// asked again is answered with the booking already made, never a second one.
// ─────────────────────────────────────────────────────────────────────

export interface VenueBookingIntent {
  court: number;
  /** Venue-local `YYYY-MM-DD`, as the availability read said it. */
  date: string;
  startTime: string;
  durationMin: number;
}

/** The booking as the door stored it — never a figure the screen worked out. */
export interface VenueBooking {
  /** The `book_…` id every later call (pay, cancel) names. */
  id: string;
  court: number;
  date: string;
  startTime: string;
  endTime: string;
  /** `pending` (owes money, held), `free`, … — the server's word. */
  paymentStatus: string;
  /** Whole kroner, as stored; null only when the server stored none. */
  priceNok: number | null;
}

export type VenueBookingOutcome =
  | {
      state: 'booked';
      booking: VenueBooking;
      /** The price the booking was made at — the door's decision, not the read's. */
      priceTier: 'member' | 'guest';
      /** How long the court is held unpaid, by the SERVER's clock (#607). */
      expiresInMs?: number;
    }
  /** The door said no, with its code (`guest_hold_limit`, `slot_not_available`, …). */
  | { state: 'refused'; code: string }
  /** No answer — the network, or a gateway. Safe to ask again: same key. */
  | { state: 'unreachable' };

export async function bookAtVenue(
  slug: string,
  intent: VenueBookingIntent,
): Promise<VenueBookingOutcome> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch(`/venues/${encodeURIComponent(slug)}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // BUILT FIELD BY FIELD: the door ignores anything else, and this is the
      // one place that decides what a venue booking sends.
      body: JSON.stringify({
        court: intent.court,
        date: intent.date,
        startTime: intent.startTime,
        durationMin: intent.durationMin,
        requestId: memberBookingRequestId(intent),
      }),
    });
  } catch {
    return { state: 'unreachable' };
  }
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    return { state: 'unreachable' };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.ok) {
    const booking = toVenueBooking(body?.booking);
    // A 200 that does not say what was booked is not something to show as a
    // booking — or to pay for.
    if (!booking) return { state: 'unreachable' };
    const expiresInMs =
      typeof body?.expiresInMs === 'number' && Number.isFinite(body.expiresInMs)
        ? body.expiresInMs
        : undefined;
    return {
      state: 'booked',
      booking,
      priceTier: body?.priceTier === 'member' ? 'member' : 'guest',
      ...(expiresInMs === undefined ? {} : { expiresInMs }),
    };
  }
  if (res.status === 401) return { state: 'refused', code: 'unauthorized' };
  const code = typeof body?.error === 'string' && body.error ? body.error : 'booking_rejected';
  return { state: 'refused', code };
}

function toVenueBooking(raw: unknown): VenueBooking | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id) return null;
  if (!Number.isInteger(r.court) || typeof r.date !== 'string' || !DAY.test(r.date)) return null;
  if (typeof r.startTime !== 'string' || typeof r.endTime !== 'string') return null;
  return {
    id: r.id,
    court: Number(r.court),
    date: r.date,
    startTime: r.startTime,
    endTime: r.endTime,
    paymentStatus: typeof r.paymentStatus === 'string' ? r.paymentStatus : '',
    priceNok: typeof r.priceNok === 'number' && Number.isFinite(r.priceNok) ? r.priceNok : null,
  };
}
